/**
 * The tenant application's entry (`app.html`).
 *
 * Four things and no more: the theme resolved once from an explicit choice or
 * the system preference, the auth provider that owns the Firebase session, the
 * query client and `ApiClient` every page reads through, and the composed
 * router. Everything else -- which routes exist, which navigation entries are
 * shown -- is discovered rather than listed, so this file does not change when
 * a feature area is added.
 *
 * The `ApiClient` is built here and passed down rather than constructed inside
 * `useMe()`, so a test drives the real client against MSW and production and
 * test differ only in the base URL and the token source.
 *
 * `AuthBridge` sits between the auth provider and the rest: it reads the
 * session contract once and hands it to `AppProviders`, which is what stops
 * `/control/me` being asked before Firebase has restored a persisted session.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';

import { AuthProvider } from './auth/AuthProvider';
import { applyTheme, resolveTheme, THEME_STORAGE_KEY } from '../tokens/theme';
import { router } from './routes';
import { AuthBridge } from './session/AuthBridge';
import { createAppApiClient } from './session/token';
import '../tokens/tokens.css';

/**
 * Storage throws rather than returning null in a browser configured to block
 * site data, and a theme preference is not worth failing to boot over.
 */
function storedTheme(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * `import.meta.env.MODE` rather than `DEV`: Vitest also runs with `DEV`
 * true (`MODE` is `'test'`), and starting a service worker under jsdom --
 * which implements no Service Worker API at all -- would fail every test
 * that boots this entry. `VITE_API_MOCKING=false` (`.env.local`, no code
 * change) opts back into a real API for a dev server run against one.
 *
 * Awaited before the first render so the router's first query cannot land
 * before the worker is registered to answer it -- MSW's own documented
 * failure mode.
 */
async function enableMocking(): Promise<void> {
  if (import.meta.env.MODE !== 'development' || import.meta.env.VITE_API_MOCKING === 'false') {
    return;
  }
  const { worker } = await import('../mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

const container = document.getElementById('app');

// Not top-level await: the build target (set for `index.html`'s unframed
// landing page) predates it, and `vite build` fails the whole bundle rather
// than downlevel one entry alone.
if (container) {
  void (async () => {
    try {
      await enableMocking();
    } catch (error) {
      // A dev-only mock worker failing to register is not worth a blank
      // page over; the app still boots, just against a real, likely-absent
      // backend.
      console.error('MSW failed to start:', error);
    }

    applyTheme(
      document.documentElement,
      resolveTheme(storedTheme(), window.matchMedia('(prefers-color-scheme: dark)').matches),
    );

    createRoot(container).render(
      <StrictMode>
        {/* Outermost: it initialises the Firebase app that `firebaseIdToken`
            reads, and the sign-in state every page below it depends on. */}
        <AuthProvider>
          <AuthBridge client={createAppApiClient()}>
            <RouterProvider router={router} />
          </AuthBridge>
        </AuthProvider>
      </StrictMode>,
    );
  })();
}
